import { ChevronDownIcon } from "@chakra-ui/icons";
import {
  Box,
  HStack,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  Link,
  VStack,
  Button,
  MenuButton,
  MenuItem,
  Menu,
  MenuList,
  Divider,
  useToast,
} from "@chakra-ui/react";
import { KYDSegment } from "common/interfaces/KYDCampaign";
import dayjs from "dayjs";
import CreateNewSegmentDialog from "./CreateNewSegmentDialog";
import { Link as RouterLink } from "react-router-dom";
import { formatDate } from "src/utils";
import { KYDScope } from "@common/interfaces/ICognito";
import ShowIfHasScope from "src/Manager/ShowIfHasScope";

const columns: {
  key: keyof any | "spacer";
  label: string;
  isNumeric?: boolean;
}[] = [
  { key: "name", label: "Name" },
  { key: "size", label: "Contacts", isNumeric: true },
  { key: "average_order_value", label: "Avg. Order Value", isNumeric: true },
  { key: "revenue", label: "Revenue", isNumeric: true },
  { key: "created_at", label: "Date Created", isNumeric: true },
  { key: "updated_at", label: "Date Updated", isNumeric: true },
  { key: "spacer", label: "-", isNumeric: true },
];

export const SegmentsTable = ({
  rows,
  updatingSegmentId,
  editingSegmentId,
  handleEdit,
  handleDuplicate,
  handleDelete,
  permissions,
}: {
  rows: KYDSegment[];
  updatingSegmentId: string | null;
  editingSegmentId: string | null;
  handleEdit: (id: string) => void;
  handleDuplicate: (id: string) => void;
  handleDelete: (segment: KYDSegment) => void;
  permissions: KYDScope[];
}) => {
  const toast = useToast();

  return (
    <Box
      w="100%"
      overflowX="auto"
      borderTop="1px solid"
      borderBottom="1px solid"
      borderColor="gray.200"
    >
      {editingSegmentId && (
        <CreateNewSegmentDialog
          isOpen={!!editingSegmentId}
          close={() => handleEdit(null)}
          segment={rows.find((row) => row.id === editingSegmentId)}
        />
      )}
      <Table variant="simple" w="100%">
        <Thead>
          <Tr height="40px">
            {columns.map(({ key, label, isNumeric }) => (
              <Th
                key={key.toString()}
                textTransform="uppercase"
                fontSize="12px"
                fontWeight="700"
                letterSpacing="0.5px"
                color="gray.700"
                pl={0}
              >
                <HStack
                  justify={isNumeric ? "center" : "flex-start"}
                  spacing={1}
                >
                  <Text whiteSpace="nowrap">{label}</Text>
                </HStack>
              </Th>
            ))}
          </Tr>
        </Thead>
        <Tbody>
          {rows.map((row) => {
            const link = `/campaigns/segments/${row.id}`;

            return (
              <Tr
                key={row.id}
                _hover={{ bg: "gray.50" }}
                style={{ textDecoration: "none", width: "100%" }}
              >
                <Td pl={0} py={8}>
                  <VStack align="flex-start" spacing={1}>
                    {row.type === "dynamic" && (
                      <Box
                        bg="#fff"
                        borderRadius="50px"
                        border="1px solid #718096"
                        color="#718096"
                        px={2}
                        fontWeight="600"
                        fontSize="12px"
                        mb={2}
                      >
                        Auto Updates
                      </Box>
                    )}
                    <Link
                      as={RouterLink}
                      to={link}
                      w="100%"
                      fontSize="14px"
                      fontWeight="500"
                      h="100%"
                      _hover={{ textDecoration: "none" }}
                    >
                      {row.name}
                    </Link>
                  </VStack>
                </Td>
                <Td textAlign="center">
                  <Link
                    as={RouterLink}
                    to={link}
                    w="100%"
                    h="100%"
                    _hover={{ textDecoration: "none" }}
                  >
                    {typeof row.size === "number"
                      ? row.size.toLocaleString()
                      : "-"}
                  </Link>
                </Td>
                <Td textAlign="center">
                  <Link
                    as={RouterLink}
                    to={link}
                    w="100%"
                    h="100%"
                    _hover={{ textDecoration: "none" }}
                  >
                    {typeof row.average_order_value === "number"
                      ? row.average_order_value.toLocaleString(undefined, {
                          maximumFractionDigits: 2,
                        })
                      : "-"}
                  </Link>
                </Td>
                <Td textAlign="center">
                  <Link
                    as={RouterLink}
                    to={link}
                    w="100%"
                    h="100%"
                    _hover={{ textDecoration: "none" }}
                  >
                    {typeof row.revenue === "number"
                      ? row.revenue.toLocaleString()
                      : "-"}
                  </Link>
                </Td>
                <Td textAlign="center">
                  <Link
                    as={RouterLink}
                    to={link}
                    w="100%"
                    h="100%"
                    _hover={{ textDecoration: "none" }}
                  >
                    {formatDate(dayjs(row.created_at))}
                  </Link>
                </Td>
                <Td textAlign="center">
                  <Link
                    as={RouterLink}
                    to={link}
                    w="100%"
                    h="100%"
                    _hover={{ textDecoration: "none" }}
                  >
                    {formatDate(dayjs(row.updated_at))}
                  </Link>
                </Td>
                <Td>
                  <ShowIfHasScope
                    permissions={permissions}
                    checkFor={["crm/campaigns:write", "crm/write"]}
                  >
                    <Menu>
                      <MenuButton
                        as={Button}
                        variant="ghost"
                        size="sm"
                        isLoading={updatingSegmentId === row.id}
                        borderRadius="6px"
                        border="1px solid #A0AEC0"
                        bg="#FFF"
                      >
                        <ChevronDownIcon color="#718096" width="16px" />
                      </MenuButton>
                      <MenuList minW="0" p={"12px"} fontSize={"13px"}>
                        <MenuItem onClick={() => handleEdit(row.id)}>
                          Edit Segment
                        </MenuItem>
                        <MenuItem onClick={() => handleDuplicate(row.id)}>
                          Duplicate Segment
                        </MenuItem>
                        <Divider borderColor={"#00000020"} my={2} />
                        <MenuItem onClick={() => handleDelete(row)}>
                          Delete Segment
                        </MenuItem>
                      </MenuList>
                    </Menu>
                  </ShowIfHasScope>
                </Td>
              </Tr>
            );
          })}
        </Tbody>
      </Table>
    </Box>
  );
};
